import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { AuthUtils } from 'app/core/auth/auth.utils';

/**
 * Guard para las pantallas del TPV offline (`/jornada/tpv-offline`).
 *
 * POR QUE EXISTE, y por que no reemplaza a `AuthGuard`:
 *
 * `AuthGuard` llama `AuthService.check()`, que hace `POST /auth/login/token`
 * para revalidar la sesion contra el servidor. En una jornada sin internet esa
 * llamada falla y el guard expulsa a `sign-in` aunque el JWT siga vigente.
 *
 * Aqui no hay red, y no hace falta: el token ya fue validado por el servidor
 * cuando se emitio, y todos los datos que muestra esta pantalla estan en el
 * IndexedDB del propio dispositivo. Lo unico que comprobamos es que el token no
 * haya expirado, que es la misma comprobacion que ya hace `AuthService.check()`
 * antes de llamar al servidor (`auth.service.ts:197`).
 *
 * ALCANCE DEL RIESGO: un JWT falsificado con `exp` lejano dejaria ver esta UI
 * en ese dispositivo, pero no reporta ventaja real — los datos son locales y
 * para sincronizar contra el servidor el token si se valida. Por eso este guard
 * se aplica SOLO a las rutas de venta, no a las que hablan con el servidor.
 *
 * Las rutas que si necesitan red (sincronizar, subir) siguen con `AuthGuard`.
 */
export const OfflineAuthGuard: CanActivateFn | CanActivateChildFn = (_route, state) => {
    const router: Router = inject(Router);

    if (tokenVigente()) {
        return true;
    }

    const redirectURL = `redirectURL=${state.url}`;
    return router.parseUrl(`sign-in?${redirectURL}`);
};

/**
 * `AuthUtils.isTokenExpired` LANZA si el token no es un JWT bien formado
 * (localStorage puede quedar con un valor a medias tras un cierre abrupto).
 * Sin este try/catch el guard reventaria y el router dejaria de navegar en
 * vez de mandar al login, que es justo lo que el optometrista necesita ver.
 */
function tokenVigente(): boolean {
    const token = localStorage.getItem('accessToken');
    if (!token) {
        return false;
    }
    try {
        return !AuthUtils.isTokenExpired(token);
    } catch {
        return false;
    }
}